import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const DARK_MODE_STORAGE_KEY = 'hop-studio-dark-mode';
const FOLLOW_SYSTEM_STORAGE_KEY = 'hop-studio-follow-system';

interface DarkModeContextType {
  isDarkMode: boolean;
  setIsDarkMode: (enabled: boolean) => void;
  toggleDarkMode: () => void;
  followSystem: boolean;
  setFollowSystem: (follow: boolean) => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

interface DarkModeProviderProps {
  children: ReactNode;
}

export const DarkModeProvider: React.FC<DarkModeProviderProps> = ({ children }) => {
  const [followSystem, setFollowSystemState] = useState<boolean>(() => {
    const stored = localStorage.getItem(FOLLOW_SYSTEM_STORAGE_KEY);
    // Default to following system if no preference is stored
    return stored === null ? true : stored === 'true';
  });

  const [isDarkMode, setIsDarkModeState] = useState<boolean>(() => {
    const followSystemStored = localStorage.getItem(FOLLOW_SYSTEM_STORAGE_KEY);
    const shouldFollowSystem = followSystemStored === null ? true : followSystemStored === 'true';
    
    if (shouldFollowSystem) {
      // Follow system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    
    // Use stored preference
    const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY);
    if (stored !== null) {
      return stored === 'true';
    }
    // Fallback to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const updateDarkModeUI = (enabled: boolean) => {
    // Update body class and color scheme
    if (enabled) {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
      document.documentElement.style.setProperty('color-scheme', 'dark');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
      document.documentElement.style.setProperty('color-scheme', 'light');
    }
  };

  const setIsDarkMode = (enabled: boolean) => {
    setIsDarkModeState(enabled);
    localStorage.setItem(DARK_MODE_STORAGE_KEY, String(enabled));
    updateDarkModeUI(enabled);
  };

  const setFollowSystem = (follow: boolean) => {
    setFollowSystemState(follow);
    localStorage.setItem(FOLLOW_SYSTEM_STORAGE_KEY, String(follow));
    
    if (follow) {
      // Immediately apply system preference
      const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkModeState(systemPreference);
      updateDarkModeUI(systemPreference);
    }
  };

  const toggleDarkMode = () => {
    if (!followSystem) {
      setIsDarkMode(!isDarkMode);
    }
  };

  // Apply dark mode on mount
  useEffect(() => {
    updateDarkModeUI(isDarkMode);
  }, [isDarkMode]);

  // Listen for system theme changes when followSystem is enabled
  useEffect(() => {
    if (!followSystem) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkModeState(e.matches);
      updateDarkModeUI(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [followSystem]);

  return (
    <DarkModeContext.Provider value={{ isDarkMode, setIsDarkMode, toggleDarkMode, followSystem, setFollowSystem }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = (): DarkModeContextType => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

